import { Injectable, signal, computed } from '@angular/core';
import { BreadcrumbItem } from '@shared/components/breadcrumb/breadcrumb.component';

/**
 * Feeds the shared `<app-breadcrumb>` its trail. Every module layout already
 * renders that component, but nothing previously bound its `items` input, so
 * no breadcrumb rendered anywhere in the app. This is opt-in: a page that
 * never calls setItems() leaves the trail empty, exactly like today, so
 * wiring one layout/page pair up has no effect on any other screen.
 *
 * onBack is optional and page-specific: a top-level list page usually
 * navigates to its module's dashboard, while a page showing a detail/sub-view
 * instead pops back to its own list state. hasBack() only turns true once a
 * page actually registers one, so `<app-breadcrumb>`'s back control never
 * renders as a dead button for a page that hasn't wired it up (e.g. an
 * existing caller still on the two-arg-less setItems()).
 */
@Injectable({ providedIn: 'root' })
export class BreadcrumbService {
  readonly items = signal<BreadcrumbItem[]>([]);
  private readonly backHandler = signal<(() => void) | null>(null);
  readonly hasBack = computed(() => this.backHandler() !== null);

  setItems(items: BreadcrumbItem[], onBack?: () => void): void {
    this.items.set(items);
    this.backHandler.set(onBack ?? null);
  }

  goBack(): void {
    this.backHandler()?.();
  }

  /** Call from the setting page's ngOnDestroy so navigating away doesn't
   *  leave a stale trail on a page that hasn't set its own. */
  clear(): void {
    this.items.set([]);
    this.backHandler.set(null);
  }
}
